import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const NotificationCard = ({
  notification,
  isSelected,
  onSelect,
  onClick,
  onMarkAsRead,
  onDelete,
  isNew = false
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isAnimating, setIsAnimating] = useState(isNew);

  // Trigger animation for new notifications
  useEffect(() => {
    if (isNew) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  const priorityColors = {
    low: 'text-blue-600 bg-blue-50',
    medium: 'text-[#FF6A00] bg-orange-50',
    high: 'text-red-600 bg-red-50'
  };

  const typeIcons = {
    job_application: 'Briefcase',
    message: 'MessageSquare',
    payment: 'DollarSign',
    profile_activity: 'User',
    system: 'Bell'
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date?.toLocaleDateString();
  };

  const handleCardClick = () => {
    onClick?.(notification);
  };

  const handleMarkAsRead = (e) => {
    e?.stopPropagation();
    onMarkAsRead?.(notification?.id);
  };

  const handleDelete = (e) => {
    e?.stopPropagation();
    onDelete?.(notification?.id);
  };

  const handleSelectToggle = (e) => {
    e?.stopPropagation();
    onSelect?.(notification?.id);
  };

  return (
    <div
      className={`
        relative p-4 hover:bg-muted/50 transition-smooth cursor-pointer border-b border-border
        ${!notification?.isRead ? 'bg-blue-50/30' : ''}
        ${isSelected ? 'bg-orange-50/50' : ''}
        ${isAnimating ? 'animate-pulse bg-green-50/50 border-l-4 border-l-green-500' : ''}
      `}
      onClick={handleCardClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {isAnimating && (
        <div className="absolute top-2 right-2">
          <span className="flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
        </div>
      )}
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="flex items-center pt-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectToggle}
            onClick={(e) => e?.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 text-[#FF6A00] focus:ring-orange-500 cursor-pointer"
          />
        </div>

        {/* Icon */}
        <div className={`
          flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
          ${priorityColors?.[notification?.priority] || 'text-gray-600 bg-gray-50'}
        `}>
          <Icon name={typeIcons?.[notification?.type] || 'Bell'} size={20} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-sm font-semibold text-foreground">
              {notification?.title}
            </h3>
            <div className="flex items-center gap-2">
              {!notification?.isRead && (
                <span className="w-2 h-2 rounded-full bg-[#FF6A00]" />
              )}
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatTimeAgo(notification?.createdAt)}
              </span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-2">
            {notification?.message}
          </p>

          {/* Priority Badge */}
          <div className="flex items-center gap-2">
            <span className={`
              inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
              ${priorityColors?.[notification?.priority] || 'text-gray-600 bg-gray-50'}
            `}>
              <Icon 
                name={
                  notification?.priority === 'high' ? 'AlertCircle' :
                  notification?.priority === 'medium'? 'AlertTriangle' : 'Info'
                } 
                size={12} 
              />
              {notification?.priority?.charAt(0)?.toUpperCase() + notification?.priority?.slice(1)}
            </span>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-1">
            {!notification?.isRead && (
              <button
                onClick={handleMarkAsRead}
                className="p-2 rounded-md hover:bg-muted transition-smooth"
                title="Mark as read"
              >
                <Icon name="Check" size={16} className="text-muted-foreground" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-2 rounded-md hover:bg-red-50 transition-smooth"
              title="Delete"
            >
              <Icon name="Trash2" size={16} className="text-red-600" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCard;